import { inject, injectable } from 'inversify';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import BaseRepository from '@/creditflowresearch/infrastructure/repository/BaseRepository';
import { IStandingsPayload, IStandingsResponse } from '@/creditflowresearch/domain/vo/League/IStandings';
import { withServerCache } from '@/creditflowresearch/infrastructure/cache/withServerCache';

@injectable()
class ScorecardRepository extends BaseRepository {
  constructor(@inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher) {
    super(rpc);
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getCompaniesAll(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_companies',
      {
        ...payload,
        filters: {
          ...payload.filters,
          includedRoles: ['ratingAgency', 'underwriter'],
        },
      },
    );
    return this.abortableCall(request, 'ratingAgencyAndUnderwriter');
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getCompaniesRatingAgency(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_companies',
      {
        ...payload,
        filters: {
          ...payload.filters,
          includedRoles: ['ratingAgency'],
        },
      },
    );
    return this.abortableCall(request, 'ratingAgency');
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getCompaniesUnderwriter(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_companies',
      {
        ...payload,
        filters: {
          ...payload.filters,
          includedRoles: ['underwriter'],
        },
      },
    );
    return this.abortableCall(request, 'underwriter');
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getCompaniesTtmFilter(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_companies.get_ttm_filter',
      {
        filters: {
          ...payload,
          includedRoles: ['ratingAgency', 'underwriter'],
        },
      },
    );
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getTopCompaniesRatingAgency(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_top_companies',
      {
        ...payload,
        filters: {
          ...payload.filters,
          includedRole: 'ratingAgency',
        },
      },
    );
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getTopCompaniesRatingAgencyTtmFilter(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_top_companies.get_ttm_filter',
      {
        filters: {
          ...payload,
          includedRole: 'ratingAgency',
        },
      },
    );
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getTopCompaniesUnderwriter(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_top_companies',
      {
        ...payload,
        filters: {
          ...payload.filters,
          includedRole: 'underwriter',
        },
      },
    );
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  }

  @withServerCache({
    scope: 'Scorecard',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getTopCompaniesUnderwriterTtmFilter(payload) {
    const request = new Request(
      'finsight_web.market.scorecard.get_top_companies.get_ttm_filter',
      {
        filters: {
          ...payload,
          includedRole: 'underwriter',
        },
      },
    );
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  }

  getStandings = async (payload: IStandingsPayload): Promise<IStandingsResponse> => {
    const request = new Request('finsight_web.market.scorecard.company.get_standings_data', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };
}

export default ScorecardRepository;
